import { useState } from 'react'

interface MemoInputModalProps {
  isOpen: boolean
  difference: number
  currencyCode: string
  onConfirm: (memo: string) => void
  onCancel: () => void
}

function MemoInputModal({
  isOpen,
  difference,
  currencyCode,
  onConfirm,
  onCancel,
}: MemoInputModalProps) {
  const [memo, setMemo] = useState('')

  if (!isOpen) return null

  const handleConfirm = () => {
    onConfirm(memo)
    setMemo('')
  }

  const handleCancel = () => {
    setMemo('')
    onCancel()
  }

  const formatDifference = (diff: number): string => {
    const sign = diff > 0 ? '+' : ''
    return `${sign}${diff.toLocaleString('ko-KR')}`
  }

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-md mx-4">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b">
          <h2 className="text-xl font-bold text-fx-text-dark">
            차액 사유 입력
          </h2>
          <button
            onClick={handleCancel}
            className="text-gray-500 hover:text-gray-700"
          >
            <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Content */}
        <div className="p-4 space-y-4">
          {/* Warning Box */}
          <div className="bg-red-50 border border-red-200 rounded-lg p-4">
            <div className="flex items-start gap-3">
              <div className="flex-shrink-0">
                <svg className="w-5 h-5 text-red-500" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                </svg>
              </div>
              <div>
                <p className="font-bold text-red-700">차액이 발생했습니다</p>
                <p className="text-red-600 text-sm mt-1">
                  시스템 잔고와 실제 잔고의 차이:{' '}
                  <span className="font-bold">{formatDifference(difference)} {currencyCode}</span>
                </p>
              </div>
            </div>
          </div>

          {/* Memo Input */}
          <div>
            <label className="block text-sm font-medium text-fx-text-muted mb-2">
              차액 발생 사유를 입력해주세요
            </label>
            <textarea
              value={memo}
              onChange={(e) => setMemo(e.target.value)}
              placeholder="예: 오전 환전 손님에게 50유로 더 지급한 것으로 추정"
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-fx-buy focus:border-transparent resize-none"
              rows={4}
            />
          </div>
        </div>

        {/* Footer */}
        <div className="flex justify-end gap-3 p-4 border-t">
          <button
            onClick={handleCancel}
            className="px-6 py-2 border border-gray-300 rounded-lg text-fx-text-dark font-medium hover:bg-gray-50 transition-all"
          >
            취소
          </button>
          <button
            onClick={handleConfirm}
            className="px-6 py-2 bg-red-500 text-white rounded-lg font-medium hover:bg-red-600 transition-all"
          >
            승인
          </button>
        </div>
      </div>
    </div>
  )
}

export default MemoInputModal
